import { Info } from "@phosphor-icons/react"
import { useState } from "react"
import {
  getExtraDataDisplayName,
  ExtraDataTable,
  pandasTypeToColType,
} from "~/utils/dynamicTableUtils"
import { Button } from "~/components/reusable/Button"
import { DataTypeBadge, DATA_TYPE_TOOLTIP } from "~/components/reusable/DataTypeBadge"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { Input } from "~/components/reusable/Input"
import Select from "~/components/reusable/Select"
import { Tooltip } from "~/components/reusable/Tooltip"

type Props = {
  updateExtradataConfiguration: (extradataConfiguration: {
    tableName: string
    fieldName: string
    userSetAggregation: string | null
    userSetDisplayName: string | null
  }) => void
  extradataConfiguration:
    | {
        tableName: string
        fieldName: string
        pandasDataType: string
        uniqueValues: string[]
        forceStringType: boolean
        userSetAggregation: string | null
        userSetDisplayName: string | null
      }
    | undefined
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
}

export const ConfigureExtradataDialog = ({
  updateExtradataConfiguration,
  extradataConfiguration,
  dialogOpen,
  setDialogOpen,
}: Props) => {
  const [tmpState, setTmpState] = useState({})
  const state = { ...extradataConfiguration, ...tmpState }
  const pandasDataType = state?.pandasDataType ?? ""
  const dataType = pandasTypeToColType(pandasDataType, state?.forceStringType ?? false)
  const changeAggregationDisabled = dataType === "string"

  const handleChangeAggregation = (val: string) => {
    setTmpState((prev) => ({ ...prev, userSetAggregation: val }))
  }

  const aggregationOptions =
    dataType === "date"
      ? [
          { label: "First", value: "first" },
          { label: "Min (earliest)", value: "min" },
          { label: "Max (latest)", value: "max" },
        ]
      : [
          { label: "First", value: "first" },
          { label: "Sum", value: "sum" },
          { label: "Avg", value: "avg" },
          { label: "Max", value: "max" },
          { label: "Min", value: "min" },
        ]

  const handleUpdateExtradataConfiguration = () => {
    if (state == null) return
    const { tableName, fieldName, userSetAggregation, userSetDisplayName } = state
    if (
      tableName == null ||
      fieldName == null ||
      (userSetAggregation === undefined && userSetDisplayName === undefined)
    )
      return

    updateExtradataConfiguration({
      tableName,
      fieldName,
      userSetAggregation: userSetAggregation ?? null,
      userSetDisplayName: (userSetDisplayName === "" ? null : userSetDisplayName) ?? null,
    })
    setDialogOpen(false)
  }

  return (
    <DialogRoot
      open={dialogOpen}
      onOpenChange={(open) => {
        setTmpState({})
        setDialogOpen(open)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-semibold flex justify-between">
            Configure{" "}
            {getExtraDataDisplayName(extradataConfiguration?.fieldName ?? "", ExtraDataTable.SKU)}{" "}
            column
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-y-5">
          <div className="flex flex-col gap-y-2">
            <div>
              <div className="flex flex-row gap-x-1.5 items-center">
                <p className="label-base">Display Name</p>
                <Tooltip body="Change the display name for this extra data column.">
                  <Info />
                </Tooltip>
              </div>
            </div>
            <Input
              value={
                state.userSetDisplayName ??
                getExtraDataDisplayName(state.fieldName ?? "", ExtraDataTable.SKU)
              }
              onChange={(e) =>
                setTmpState((prev) => ({ ...prev, userSetDisplayName: e.target.value }))
              }
              containerClassName="w-full"
            />
            {state.userSetDisplayName === "" && (
              <p className="paragraph-sm text-fg-subtle">
                The default name{" "}
                {getExtraDataDisplayName(state.fieldName ?? "", ExtraDataTable.SKU)} will be used
                when the field is empty.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row gap-x-1.5 items-center">
              <p className="label-base">Data Type</p>
              <Tooltip body={DATA_TYPE_TOOLTIP}>
                <Info />
              </Tooltip>
            </div>
            <DataTypeBadge dataType={dataType} />
          </div>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row gap-x-1.5 items-center">
              <p className="label-base">Aggregation</p>
              <Tooltip body="Change the method of aggregation from SKU to Variant for this extradata column. It is currently only possible to change the aggregation method for numerical columns.">
                <Info />
              </Tooltip>
            </div>
            <Select
              value={aggregationOptions.find(
                (o) => o.value === (state.userSetAggregation ?? "first"),
              )}
              isDisabled={changeAggregationDisabled}
              options={aggregationOptions}
              onChange={(option) => option && handleChangeAggregation(option.value)}
            />
            {changeAggregationDisabled && (
              <p className="paragraph-sm text-fg-subtle">
                Editing of aggregation is disabled for text columns
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="flex gap-x-3 justify-end">
          <DialogClose onClick={() => setDialogOpen(false)}>
            <Button asChild variant="neutral">
              Cancel
            </Button>
          </DialogClose>
          <Button requires="edit" onClick={handleUpdateExtradataConfiguration}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
